import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useQueryClient } from "@tanstack/react-query";

import { TextField } from "@/components/form/TextField";
import { useAutosaveForm } from "@/components/form/useAutosaveForm";
import { zodField } from "@/components/form/validators";
import { SaveStatus } from "@/components/SaveStatus";
import type { ApiError } from "@/lib/api/unwrap";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import {
  billingKeys,
  saveBillingInformation,
  useBillingInformation,
  type BillingInformation,
} from "./api";

// Concrete all-string shape: the API fields are optional (blank-tolerant since
// F-3), but the form always works with strings (empty when unset).
type BillingFormValues = {
  name: string;
  city: string;
  address: string;
  province: string;
  zip_code: string;
};

const required = (message: string) => z.string().trim().min(1, message);

// Canadian postal code, mirroring the server-side is_valid_canadian_postal_code
// (F-6) so client + API agree; the guard below keeps an invalid value from being
// autosaved (which would 400 the whole PATCH and block the co-saved fields).
// Case-insensitive to match the server (re.IGNORECASE) — a lowercase code like
// "g7h 7k9" is accepted server-side, so the client must not block it.
const CANADIAN_POSTAL_RE =
  /[ABCEGHJKLMNPRSTVXY][0-9][ABCEGHJKLMNPRSTVWXYZ] ?[0-9][ABCEGHJKLMNPRSTVWXYZ][0-9]/i;
const isValidPostal = (v: string) => CANADIAN_POSTAL_RE.test(v.trim());

/**
 * Billing-information form with on-change autosave.
 *
 * Each keystroke feeds the debounced autosave (abort-on-collision + visible
 * <SaveStatus>) which PATCHes /billing/information/. No sendBeacon flush is
 * wired because that endpoint only accepts PATCH/PUT and sendBeacon can only
 * POST — the debounced save + unmount abort cover the realistic loss window.
 */
function BillingForm({ initial }: { initial: BillingInformation }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Server-side field errors (e.g. a billing name another account already
  // holds). The API answers 400 with the message under the offending field, so
  // show it there, the way the Django billing form does.
  const [serverErrors, setServerErrors] = useState<
    Partial<Record<keyof BillingFormValues, string>>
  >({});

  const { form, autosave } = useAutosaveForm<BillingFormValues>({
    defaultValues: {
      name: initial.name ?? "",
      city: initial.city ?? "",
      address: initial.address ?? "",
      province: initial.province ?? "",
      zip_code: initial.zip_code ?? "",
    },
    autosave: {
      save: async (data, signal) => {
        const updated = await saveBillingInformation(data, signal);
        // Refresh the cached billing info so the payment-card gate re-evaluates
        // (the card fields appear once the info is complete).
        queryClient.setQueryData(billingKeys.info(), updated);
        return updated;
      },
      // Require the company name before saving: the row is keyed on a globally
      // unique name, so the server won't create it until a real name is present
      // (F-a). Saving address/city first would no-op server-side and cache
      // non-persisted state.
      shouldSave: (data) =>
        Boolean(data.name.trim()) &&
        (data.zip_code.trim() === "" || isValidPostal(data.zip_code)),
      onSaved: () => setServerErrors({}),
      onError: (err) => {
        const fieldErrors = (err as ApiError)?.fieldErrors ?? {};
        setServerErrors(
          Object.fromEntries(
            Object.entries(fieldErrors).map(([key, messages]) => [
              key,
              messages.join(" "),
            ]),
          ),
        );
      },
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle>{t("billing.info.title")}</CardTitle>
          <SaveStatus status={autosave.status} />
        </div>
        <CardDescription>{t("billing.info.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit();
          }}
          className="space-y-4"
        >
          <form.Field
            name="name"
            validators={{ onChange: zodField(required(t("billing.info.nameRequired"))) }}
          >
            {(field) => (
              <TextField
                field={field}
                serverError={serverErrors.name}
                label={t("billing.info.name")}
                autoComplete="organization"
              />
            )}
          </form.Field>

          <form.Field
            name="address"
            validators={{
              onChange: zodField(required(t("billing.info.addressRequired"))),
            }}
          >
            {(field) => (
              <TextField
                field={field}
                serverError={serverErrors.address}
                label={t("billing.info.address")}
                autoComplete="street-address"
              />
            )}
          </form.Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <form.Field
              name="city"
              validators={{
                onChange: zodField(required(t("billing.info.cityRequired"))),
              }}
            >
              {(field) => (
                <TextField
                  field={field}
                  serverError={serverErrors.city}
                  label={t("billing.info.city")}
                  autoComplete="address-level2"
                />
              )}
            </form.Field>

            <form.Field name="province">
              {(field) => (
                <TextField
                  field={field}
                  serverError={serverErrors.province}
                  label={t("billing.info.province")}
                  autoComplete="address-level1"
                />
              )}
            </form.Field>
          </div>

          <form.Field
            name="zip_code"
            validators={{
              onChange: zodField(
                z
                  .string()
                  .trim()
                  .min(1, t("billing.info.zipRequired"))
                  .regex(CANADIAN_POSTAL_RE, t("billing.info.zipInvalid")),
              ),
            }}
          >
            {(field) => (
              <TextField
                field={field}
                serverError={serverErrors.zip_code}
                label={t("billing.info.zipCode")}
                autoComplete="postal-code"
                className="sm:max-w-[12rem]"
              />
            )}
          </form.Field>
        </form>
      </CardContent>
    </Card>
  );
}

export function BillingInformationForm() {
  const { t } = useTranslation();
  const { data, isPending, isError } = useBillingInformation();

  if (isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("billing.info.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{t("billing.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("billing.info.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">{t("billing.loadError")}</p>
        </CardContent>
      </Card>
    );
  }

  // Key on the record id so the form re-initializes if the underlying record
  // identity changes (e.g. after a fresh get_or_create).
  return <BillingForm key={data.id} initial={data} />;
}
